<ng-container *ngIf="!isLoading; else loadingTemplate">
  <div class="page">
    <header class="header">
      <div class="title">
        <h1>User Management</h1>
        <p>Manage and monitor all registered users</p>
      </div>
      <div class="total-pill">{{ totalUsers() }} users</div>
    </header>

    <div class="controls">
      <div class="search">
        <span class="icon">🔎</span>
        <input
          type="text"
          placeholder="Search by name or email"
          [ngModel]="searchTerm()"
          (ngModelChange)="searchTerm.set($event); page.set(1)"
        />
      </div>

      <label class="toggle">
        <input type="checkbox" [checked]="withApplicationOnly()" (change)="withApplicationOnly.set($event.target.checked); page.set(1)" />
        <span class="slider"></span>
        <span class="label">With Application</span>
      </label>

      <div class="select">
        <span>Country</span>
        <select  [ngModel]="selectedCountry()" (ngModelChange)="selectedCountry.set($event); page.set(1)">
          <option value="">All</option>
          <option *ngFor="let c of countries()" [value]="c">{{ c }}</option>
        </select>
      </div>

      <div class="select">
        <span>Language Level</span>
        <details>
          <summary>{{ selectedLangs().size ? selectedLangs().size : '' }} {{ selectedLangs().size ? 'selected' : 'All' }}</summary>
          <div class="menu">
            <button class="chip" *ngFor="let l of languageLevels()"
                    [class.active]="selectedLangs().has(l)"
                    (click)="toggleLang(l)">
              {{ l }}
            </button>
          </div>
        </details>
      </div>

      <div class="select">
        <span>Looking For</span>
        <select
          [ngModel]="selectedLookingFor()"
          (ngModelChange)="selectedLookingFor.set($event); page.set(1)"
        >
          <option value="">All</option>
          <option *ngFor="let lf of lookingForOptions()" [value]="lf">{{ prettyLabel(lf) }}</option>
        </select>
      </div>

      <div class="select">
        <span>Domain</span>
        <select
          [ngModel]="selectedDomain()"
          (ngModelChange)="selectedDomain.set($event); page.set(1)"
        >
          <option value="">All</option>
          <option *ngFor="let d of domainOptions()" [value]="d">{{ d }}</option>
        </select>
      </div>

      <div class="select">
        <span>Sort By</span>
        <select [ngModel]="sortBy()" (ngModelChange)="sortBy.set($event)">
          <option value="recent">Recently Registered</option>
          <option value="name">Name (A–Z)</option>
          <option value="applications">Applications (High→Low)</option>
        </select>
      </div>
    </div>

    <div class="chips-row">
      <button class="link" (click)="clearFilters()">Reset filters</button>
    </div>

    <div class="table">
      <div class="row head">
        <div class="col user">User</div>
        <div class="col email">Email</div>
        <div class="col country">Country</div>
        <div class="col reg">Registered</div>
        <div class="col lang">Language</div>
        <div class="col lookfor">Looking For</div>
        <div class="col dom">Domain</div>
        <div class="col apps">Applications</div>
        <div class="col actions"></div>
      </div>

      <ng-container *ngFor="let u of paginatedUsers(); let i = index; trackBy: trackByUser">
        <div class="row" [class.row-expanded]="isExpanded(u.id)">
          <div class="col user">
            <img *ngIf="u.profileImageUrl; else initialsTpl" [src]="u.profileImageUrl" class="avatar" alt="avatar">
            <ng-template #initialsTpl>
              <span class="avatar avatar-initials">{{ initials(u) }}</span>
            </ng-template>
            <div class="info">
              <a class="name" (click)="viewProfile(u.id)">{{ displayName(u) }}</a>
            </div>
          </div>

          <div class="col email">
            <a *ngIf="u.email; else noEmail" class="email-link" [href]="'mailto:' + u.email" [title]="u.email">{{ u.email }}</a>
            <ng-template #noEmail><span class="muted">—</span></ng-template>
          </div>

          <div class="col country" [title]="u.country || ''">
            <span *ngIf="u.country; else dashCountry">{{ u.country }}</span>
            <ng-template #dashCountry><span class="muted">—</span></ng-template>
          </div>

          <div class="col reg">
            {{ registeredAt(u) }}
          </div>

          <div class="col lang">
            <span class="badge" *ngIf="u.languageLevel; else dashLang">{{ u.languageLevel }}</span>
            <ng-template #dashLang><span class="muted">—</span></ng-template>
          </div>

          <div class="col lookfor" [title]="prettyLabel(u.lookingFor)">
            {{ prettyLabel(u.lookingFor) }}
          </div>

          <div class="col dom" [title]="u.domain || ''">
            {{ u.domain || '—' }}
          </div>

          <div class="col apps">
            <ng-container *ngIf="(u.applicationsCount ?? 0) > 0; else noapps">
              <span class="app-pill">{{ u.applicationsCount }}</span>
              <button
                class="expand-apps"
                (click)="toggleApplications(u.id)"
                [title]="isExpanded(u.id) ? 'Collapse' : 'View applied positions'"
              >{{ isExpanded(u.id) ? '▼' : '▶' }}</button>
            </ng-container>
            <ng-template #noapps><span class="muted">No applications</span></ng-template>
          </div>

          <div class="col actions">
            <button class="view" title="View profile" (click)="viewProfile(u.id)">👁</button>

            <div class="menu-wrap" [class.open]="openMenuIndex() === i">
              <button class="kebab" (click)="openMenuIndex.set(openMenuIndex() === i ? null : i)">⋮</button>

              <div class="menu" *ngIf="openMenuIndex() === i">
                <button class="danger" (click)="removeUser(u)">🗑 Remove user</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Expanded applications detail -->
        <div class="apps-detail" *ngIf="isExpanded(u.id)">
          <ng-container *ngIf="isLoadingApps(u.id)">
            <span class="muted loading-apps">Loading applications…</span>
          </ng-container>

          <ng-container *ngIf="!isLoadingApps(u.id) && userApplicationDetails().get(u.id) as apps">
            <div class="apps-mini-table">
              <div class="apps-mini-row apps-mini-head">
                <div>Position</div>
                <div>Company</div>
                <div>Location</div>
                <div>Status</div>
                <div>Applied</div>
              </div>
              <div class="apps-mini-row" *ngFor="let app of apps">
                <div class="app-title" [title]="app.jobOffer?.title">{{ app.jobOffer?.title || '—' }}</div>
                <div class="app-company" [title]="app.jobOffer?.companyName">{{ app.jobOffer?.companyName || '—' }}</div>
                <div class="app-location">{{ app.jobOffer?.location || app.location || '—' }}</div>
                <div>
                  <span class="status-badge" [ngClass]="'status-' + app.status.toLowerCase()">{{ prettyLabel(app.status) }}</span>
                </div>
                <div class="app-date">{{ formatDate(app.appliedAt) }}</div>
              </div>
              <div class="apps-mini-empty" *ngIf="!apps.length">No application data available.</div>
            </div>
          </ng-container>
        </div>
      </ng-container>

      <div class="row empty" *ngIf="filteredUsers().length === 0">
        <span class="muted">No users match the current filters.</span>
      </div>
    </div>

    <div class="footer">
      <span>
        Showing
        {{ filteredUsers().length ? (page()-1) * pageSize() + 1 : 0 }}
        –
        {{ Math.min(page() * pageSize(), filteredUsers().length) }}
        of
        {{ filteredUsers().length }}
        users
      </span>

      <div class="pager">
        <button (click)="prevPage()" [disabled]="page() === 1">Previous</button>
        <button (click)="nextPage()" [disabled]="page() * pageSize() >= filteredUsers().length">Next</button>
      </div>
    </div>
  </div>
</ng-container>

<ng-template #loadingTemplate>
  <app-load-mask></app-load-mask>
</ng-template>
